import { UserRole } from "@/lib/api/user";
import {
  isImpersonatedSession,
  readImpersonationToken,
} from "@/lib/impersonation";
import type { Session } from "@stytch/vanilla-js";

export type AuthLayoutRedirect =
  | { to: "/impersonate"; impersonationToken: string }
  | { to: "/" }
  | null;

interface AuthLayoutInput {
  searchStr: string;
  session: Session | null | undefined;
  user: { id?: string; role?: UserRole } | null | undefined;
  pathname?: string;
}

/**
 * Decides where the `_auth` layout should send an incoming request
 */
export const resolveAuthLayoutRedirect = ({
  searchStr,
  session,
  user,
  pathname,
}: AuthLayoutInput): AuthLayoutRedirect => {
  const impersonationToken = readImpersonationToken(searchStr);

  if (impersonationToken) {
    return { to: "/impersonate", impersonationToken };
  }

  // Accept-invite must finish password setup after OTP (session becomes 2FA
  // mid-flow). Bouncing to `/` here races the navigate to `/mentor-profile`.
  if (pathname?.startsWith("/accept-invite")) {
    return null;
  }

  const hasRequiredFactors =
    (session?.authentication_factors.length ?? 0) >= 2 ||
    isImpersonatedSession(session);

  const isSignedIn =
    !!session?.session_id &&
    hasRequiredFactors &&
    !!user?.id &&
    user.role !== UserRole.MENTEE;

  return isSignedIn ? { to: "/" } : null;
};
